import { SimpleOrgMember } from '@euler/model/SimpleOrgMember';
import { BiDatasetVisibility } from '@euler/model/bi/dataset';
import {
  BiDatasetAudience,
  BiDatasetGrantInput,
} from '@euler/model/bi/library';
import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useEffect, useRef, useState } from 'react';
import { biSameDatasetGrants } from './biSameDatasetGrants';

export type BiDatasetSharingState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      /** The sharing as it was when the panel opened: what edits start from. */
      visibility: BiDatasetVisibility;
      grants: BiDatasetGrantInput[];
      /** The store's members: who can be granted, and their names. */
      members: SimpleOrgMember[];
    }
  | { kind: 'failed' };

export interface BiDatasetSharing {
  visibility: BiDatasetVisibility;
  grants: BiDatasetGrantInput[];
}

/**
 * What a save did: everything applied, or a widening the server holds
 * until it is confirmed — with the audience it would reach.
 */
export type BiDatasetSharingOutcome =
  | { kind: 'applied' }
  | {
      kind: 'confirm';
      audience: BiDatasetAudience;
      /** A narrowing went through before the widening stopped. */
      partly: boolean;
    };

/** A dataset's grants and the store's members, read when the panel opens. */
export function useBiDatasetSharing(
  datasetId: string,
  /** The dataset's visibility as the page last read it. */
  visibility: BiDatasetVisibility,
  visible: boolean,
) {
  const { biService, storeService } = useServiceFactory();
  // Read when the panel opens, not on every refresh of the page: a
  // narrowing applied before a confirmation must not reset the edits.
  const visibilityRef = useRef(visibility);
  visibilityRef.current = visibility;
  const [state, setState] = useState<BiDatasetSharingState>({
    kind: 'loading',
  });
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!visible) return;
    let active = true;
    setState({ kind: 'loading' });
    Promise.all([
      biService.datasetGrants(datasetId),
      storeService.getSimpleMemberList(),
    ])
      .then(([grants, members]) => {
        if (!active) return;
        setState({
          kind: 'ready',
          visibility: visibilityRef.current,
          grants: grants.map(({ memberId, permission }) => ({
            memberId,
            permission,
          })),
          members,
        });
      })
      .catch(error => {
        console.error('BI dataset sharing failed', datasetId, error);
        if (active) setState({ kind: 'failed' });
      });
    return () => {
      active = false;
    };
  }, [biService, storeService, datasetId, visible, reload]);
  const retry = useCallback(() => setReload(value => value + 1), []);

  /**
   * Writes what changed, the visibility first. A narrowing applies at once;
   * a widening is only answered with its audience, and nothing after it is
   * sent, until `confirmed` sends it all again (resending what already
   * applied changes nothing).
   */
  const save = useCallback(
    async (
      before: BiDatasetSharing,
      after: BiDatasetSharing,
      confirmed: boolean,
    ): Promise<BiDatasetSharingOutcome> => {
      let partly = false;
      if (after.visibility !== before.visibility) {
        const result = await biService.setDatasetVisibility(
          datasetId,
          after.visibility,
          confirmed,
        );
        if (!result.applied)
          return { kind: 'confirm', audience: result.audience, partly };
        partly = true;
      }
      if (!biSameDatasetGrants(before.grants, after.grants)) {
        const result = await biService.replaceDatasetGrants(
          datasetId,
          after.grants,
          confirmed,
        );
        if (!result.applied)
          return { kind: 'confirm', audience: result.audience, partly };
      }
      return { kind: 'applied' };
    },
    [biService, datasetId],
  );
  return { state, retry, save };
}
